// Substrate library public API.
//
// Whitelabel packages import from '@whittlelabs/shuttle' and call
// `createCli(brand)`. Most other exports support tests, ad-hoc tooling,
// and brand `setupSteps`.

export { createCli } from './cli';
export { Shuttle } from './core/shuttle';
export { loadConfig } from './config/loader';
export type { ShuttleConfig } from './config/schema';

// Backend version-floor contract.
export {
  MIN_VERSION_HEADER,
  UpgradeRequiredError,
  compareSemver,
  checkResponseMinVersion,
} from './version-check';

// Credential-revocation contract: Keep refused the paired agent's
// credentials outright (decommissioned or secret rotated). Re-exported
// so whitelabel bins can map it to a dedicated exit code without
// depending on @whittlelabs/keep directly.
export { AgentCredentialRevokedError } from '@whittlelabs/keep';

// Executors (substrate-shipped + types).
export { ExecutorRegistry } from './executors/registry';
export type {
  JobExecutor,
  ExecutorCapability,
  CostHint,
  ExecutorResponse,
  ExecutorFactory,
} from './executors/types';
export { createClaudeCodeExecutor } from './executors/claude-code';
export { createHttpApiExecutor } from './executors/http-api';
export { createWebhookExecutor } from './executors/webhook';
export { createCustomScriptExecutor } from './executors/custom-script';

// Observers. The substrate ships logger + audit-log + spend-tracker; the chain
// primitive and event types live here (ADR 0005), not in `@whittlelabs/jobs`.
export {
  ObserverChain,
  SpendCapExceeded,
  type LifecycleEvent,
  type LifecycleObserver,
} from './observers/chain';
export { createLoggerObserver } from './observers/logger';
export { createAuditLogObserver } from './observers/audit-log';
export {
  createSpendTrackerObserver,
  type SpendTrackerHandle,
  type UsageSnapshot,
} from './observers/spend-tracker';

// Persistent state stores (used by CLI tools and tests).
export { AuditLogStore } from './storage/audit-log-store';
export {
  SpendStore,
  type SpendCapsState,
  type SpendStateDocument,
  type SpendEntry,
} from './storage/spend-store';

// Branding.
export { resolvePaths, expandHome, renderTemplate } from './branding/apply';
export type { BrandedPaths } from './branding/apply';
export {
  SUBSTRATE_BRAND,
  DEFAULT_DAILY_PERIOD,
  DEFAULT_MONTHLY_PERIOD,
  DEFAULT_REFUSAL_MESSAGE,
} from './branding/defaults';
export type {
  BrandConfig,
  BrandProduct,
  BrandPaths,
  BrandStrings,
  KeepRegistration,
  DefaultCaps,
  AuditBrandingOptions,
  CapsBrandingOptions,
  PeriodSpec,
  AuditRecord,
  SetupStep,
  SetupStepContext,
  SetupOptions,
} from './branding/types';

// Pairing.
export { pair } from './pairing/device-code';
export {
  PairingConfigStore,
  type PairingConfig,
} from './pairing/store';
export {
  listPairings,
  revokePairing,
  type PairedMachine,
} from './pairing/pairings';
